import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ListMusic, Play, FolderOpen } from 'lucide-react';
import { formatModeLabel } from '../utils/formatters';

/**
 * Dialog for selecting where to add uploaded tracks
 * Options: Queue, Current Part, or Choose specific Part
 */
const AddTrackDestinationDialog = ({ 
  open, 
  onClose, 
  onSelectDestination,
  parts = [],
  currentPartId = null 
}) => {
  const [selectedPartId, setSelectedPartId] = useState(currentPartId);
  const [rememberChoice, setRememberChoice] = useState(false);

  const handleDestinationSelect = (destination) => {
    if (rememberChoice) {
      localStorage.setItem('preferredUploadDestination', destination);
    }
    onSelectDestination(destination, rememberChoice);
  };

  const handleConfirm = () => {
    if (selectedPartId) {
      handleDestinationSelect(selectedPartId);
    }
  };

  const currentPart = parts.find(p => p.id === currentPartId);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-dark-card border-sunset-orange/30">
        <DialogHeader>
          <DialogTitle className="text-purple-200 flex items-center gap-2">
            <FolderOpen className="w-5 h-5" />
            Où ajouter les titres ?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {/* Option 1: Add to Queue */}
          <Button
            onClick={() => handleDestinationSelect('queue')}
            variant="outline"
            className="w-full justify-start gap-3 h-auto py-4 border-blue-500/30 hover:bg-blue-500/10"
          >
            <ListMusic className="w-5 h-5 text-blue-400" />
            <div className="text-left">
              <div className="font-semibold text-blue-300">File d'attente</div>
              <div className="text-xs text-purple-300/80">
                Ajouter à la queue pour lecture immédiate
              </div>
            </div>
          </Button>

          {/* Option 2: Add to Current Part */}
          {currentPart && (
            <Button
              onClick={() => handleDestinationSelect(currentPartId)}
              variant="outline"
              className="w-full justify-start gap-3 h-auto py-4 border-green-500/30 hover:bg-green-500/10"
            >
              <Play className="w-5 h-5 text-green-400" />
              <div className="text-left">
                <div className="font-semibold text-green-300">Partie en cours</div>
                <div className="text-xs text-purple-300/80">
                  {currentPart.name} (lecture active)
                </div>
              </div>
            </Button>
          )}

          {/* Option 3: Choose Specific Part */}
          <div className="border border-sunset-orange/30 rounded-lg p-3">
            <div className="text-sm font-semibold text-purple-200 mb-2">
              Choisir une partie spécifique:
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {parts.map((part) => (
                <button
                  key={part.id}
                  onClick={() => setSelectedPartId(part.id)}
                  className={`w-full text-left px-3 py-2 rounded transition-colors ${
                    selectedPartId === part.id
                      ? 'bg-purple-600 text-white'
                      : 'bg-dark-input/50 text-gray-300 hover:bg-slate-700'
                  }`}
                >
                  <div className="font-medium">{part.name}</div>
                  <div className="text-xs opacity-70">{formatModeLabel(part.mode)}</div>
                </button>
              ))}
            </div>
            {selectedPartId && selectedPartId !== currentPartId && (
              <Button
                onClick={handleConfirm}
                className="w-full mt-3 bg-purple-600 hover:bg-purple-700"
              >
                Ajouter à "{parts.find(p => p.id === selectedPartId)?.name}"
              </Button>
            )}
          </div>
        </div>

        {/* Remember Choice Checkbox */}
        <div className="flex items-center gap-2 pt-2 border-t border-slate-700/50">
          <input
            type="checkbox"
            id="rememberChoice"
            checked={rememberChoice}
            onChange={(e) => setRememberChoice(e.target.checked)}
            className="w-4 h-4 rounded border-gray-600 bg-dark-input text-purple-600 focus:ring-purple-500"
          />
          <label htmlFor="rememberChoice" className="text-sm text-purple-300/80 cursor-pointer">
            💾 Mémoriser mon choix pour la prochaine fois
          </label>
        </div>

        <Button
          onClick={onClose}
          variant="ghost"
          className="w-full"
        >
          Annuler
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default AddTrackDestinationDialog;
